import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, ChevronRight, X, Check } from "lucide-react";
import { api } from "../lib/api";

const COLORS = ["#E8A5B7","#B7D4C7","#D4C4A0","#C4A9D4","#A9C8F1","#D4B7A9","#B8B8B8","#F1D4A9","#F1A9B7","#A9F1DC"];

export default function LifeAreas() {
  const [areas, setAreas] = useState([]);
  const [editing, setEditing] = useState(null); // area object being edited
  const [newSubName, setNewSubName] = useState({});
  const [addingArea, setAddingArea] = useState(false);
  const [newAreaName, setNewAreaName] = useState("");
  const [newAreaColor, setNewAreaColor] = useState("#A9C8F1");
  const [expanded, setExpanded] = useState({});

  const load = () => api.listAreas().then(setAreas).catch(() => toast.error("Errore"));
  useEffect(() => { load(); }, []);

  const saveArea = async () => {
    try {
      await api.updateArea(editing.id, {
        name: editing.name, color: editing.color, icon: editing.icon,
        sort_order: editing.sort_order, is_active: editing.is_active,
      });
      toast.success("Area aggiornata");
      setEditing(null); await load();
    } catch { toast.error("Errore"); }
  };
  const removeArea = async (id) => {
    if (!confirm("Eliminare quest'area? Le sotto-aree collegate saranno rimosse.")) return;
    try { await api.deleteArea(id); toast.success("Area eliminata"); await load(); }
    catch { toast.error("Errore"); }
  };
  const addArea = async () => {
    if (!newAreaName.trim()) return;
    try {
      await api.createArea({ name: newAreaName.trim(), color: newAreaColor, sort_order: areas.length });
      setNewAreaName(""); setAddingArea(false); await load();
      toast.success("Area creata");
    } catch { toast.error("Errore"); }
  };
  const addSub = async (areaId) => {
    const nm = (newSubName[areaId] || "").trim();
    if (!nm) return;
    try {
      await api.createSubArea({ area_id: areaId, name: nm, sort_order: 0 });
      setNewSubName({ ...newSubName, [areaId]: "" }); await load();
    } catch { toast.error("Errore"); }
  };
  const removeSub = async (id) => { try { await api.deleteSubArea(id); await load(); } catch { toast.error("Errore"); } };

  return (
    <div className="space-y-6" data-testid="life-areas-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest opacity-70">Struttura</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold">Aree della vita</h1>
          <p className="mt-2 text-sm opacity-80 max-w-2xl">
            Le 8 aree predefinite sono state create al primo accesso. Puoi rinominarle, cambiare colore o disattivarle. Aggiungi sotto-aree per organizzare meglio task e progetti.
          </p>
        </div>
        <button className="pxos-btn-primary text-sm" onClick={() => setAddingArea(true)} data-testid="add-area-btn"><Plus size={14}/> Nuova area</button>
      </div>

      {addingArea && (
        <div className="pxos-card p-4 space-y-3" data-testid="area-form-new">
          <div className="flex gap-3">
            <input autoFocus className="pxos-input flex-1" placeholder="Nome area"
                   value={newAreaName} onChange={(e) => setNewAreaName(e.target.value)} data-testid="area-new-name" />
            <select className="pxos-input w-40" value={newAreaColor} onChange={(e) => setNewAreaColor(e.target.value)} data-testid="area-new-color">
              {COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button className="pxos-btn-primary text-sm" onClick={addArea} data-testid="area-new-submit"><Check size={14}/> Crea</button>
            <button className="text-sm underline opacity-70" onClick={() => setAddingArea(false)} data-testid="area-new-cancel">Annulla</button>
          </div>
        </div>
      )}

      <div className="grid gap-3">
        {areas.map((a) => (
          <div key={a.id} className="pxos-card p-4" data-testid="area-row">
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 rounded-full" style={{ background: a.color || "#B8B8B8" }} />
              {editing?.id === a.id ? (
                <>
                  <input className="pxos-input flex-1" value={editing.name}
                         onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="area-edit-name" />
                  <select className="pxos-input w-40" value={editing.color || ""}
                          onChange={(e) => setEditing({ ...editing, color: e.target.value })} data-testid="area-edit-color">
                    {COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <label className="text-xs flex items-center gap-1">
                    <input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Attiva
                  </label>
                  <button onClick={saveArea} className="p-2 hover:bg-black/5 rounded-full" data-testid="area-edit-save"><Check size={16}/></button>
                  <button onClick={() => setEditing(null)} className="p-2 hover:bg-black/5 rounded-full" data-testid="area-edit-cancel"><X size={16}/></button>
                </>
              ) : (
                <>
                  <button onClick={() => setExpanded({ ...expanded, [a.id]: !expanded[a.id] })}
                          className="flex items-center gap-2 flex-1 text-left">
                    <ChevronRight size={16} className={`transition ${expanded[a.id] ? "rotate-90" : ""}`} />
                    <div className="font-medium" data-testid="area-name">{a.name}</div>
                    {!a.is_active && <span className="pxos-badge">Disattiva</span>}
                    {a.is_seeded && <span className="pxos-badge opacity-60 text-[10px]">seed</span>}
                    <span className="text-xs opacity-60 ml-2">{a.sub_areas?.length || 0} sotto-aree</span>
                  </button>
                  <button onClick={() => setEditing(a)} className="p-2 hover:bg-black/5 rounded-full" data-testid="area-edit-btn"><Pencil size={15}/></button>
                  <button onClick={() => removeArea(a.id)} className="p-2 hover:bg-black/5 rounded-full text-red-600" data-testid="area-delete-btn"><Trash2 size={15}/></button>
                </>
              )}
            </div>
            {expanded[a.id] && (
              <div className="mt-3 ml-7 space-y-2" data-testid="sub-areas-list">
                {a.sub_areas?.map((s) => (
                  <div key={s.id} className="flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 rounded-full" style={{ background: s.color || a.color || "#B8B8B8" }}/>
                    <span className="flex-1" data-testid="sub-area-name">{s.name}</span>
                    <button onClick={() => removeSub(s.id)} className="opacity-60 hover:text-red-600" data-testid="sub-area-delete-btn"><Trash2 size={13}/></button>
                  </div>
                ))}
                <div className="flex gap-2 pt-1">
                  <input className="pxos-input text-sm flex-1" placeholder="Nuova sotto-area…"
                         value={newSubName[a.id] || ""}
                         onChange={(e) => setNewSubName({ ...newSubName, [a.id]: e.target.value })}
                         onKeyDown={(e) => e.key === "Enter" && addSub(a.id)}
                         data-testid="sub-area-input" />
                  <button className="pxos-btn-primary text-sm" onClick={() => addSub(a.id)} data-testid="sub-area-add-btn"><Plus size={14}/></button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
